import { useMediaQuery } from '@mui/material';
import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { Icon, Icons, Tooltip, Typography } from '../../../stories/atoms';
import { ContainerV2, RowFlexContainer } from '../../../stories/atoms/containerV2/ContainerV2';
import { Switch } from '../../../stories/molecules/switch/Switch';
import { muiTheme } from '../../../theme';
import { widgetAccessControlTranslations } from '../../../translations';
import { isFailureResponse, Translation } from '../../../utils';
import { AreaWidgetType } from '../../areaOverview/util';
import { WidgetType } from '../../home/types';
import { StyledTypography } from '../WidgetAccessControlPage.styles';
import { useAsyncAction, useStores, useUser } from '../../../hooks';
import { KpiType } from '../../../constants';
import { kpiTypeToWidgetTypeMap } from '../../home/util';
import { HighlightedWidgetModal } from '../highlightWidgetModal/HighlightedWidgetModal';
import { StyledHighlightIcon } from '../../kpiTarget/kpiCard/KpiCard.styles';

const getKpiTypeFromWidgetType = (widgetType: WidgetType): KpiType => {
  let kpiType = '' as KpiType;

  Object.entries(kpiTypeToWidgetTypeMap).map((val) => {
    const [key, value] = val;

    if (value === widgetType) {
      kpiType = key as KpiType;
    }
  });
  return kpiType;
};
type WidgetSwitchProps = {
  widgetType: WidgetType | AreaWidgetType;
  shouldShowHighlightIcon: boolean;
  isPermissionDisabled: boolean;
  isSwitchActive: boolean;
  isSwitchDisabled: boolean;
  translationData: Translation;
  onSwitchToggle: (widgetType: WidgetType | AreaWidgetType) => void;
};

export const WidgetSwitch: FC<WidgetSwitchProps> = observer(
  ({
    widgetType,
    isPermissionDisabled,
    isSwitchActive,
    isSwitchDisabled,
    translationData,
    onSwitchToggle,
    shouldShowHighlightIcon,
  }) => {
    const {
      set,
      get: { highlightedKpiTargets },
    } = useUser();
    const [showHighlightedWidgetModal, setShowHighlightedWidgetModal] = useState(false);
    const { updateHighlightedKpi } = useStores();
    const {
      isLoading,
      isError,
      result: updatedHighlightKpiTypes,
      triggerAction: triggerHighlightKpi,
      reset: resetHighlightKpi,
    } = useAsyncAction<KpiType[]>();
    const isWidgetHighlighted = useMemo(() => {
      const kpiType = getKpiTypeFromWidgetType(widgetType as WidgetType);
      return highlightedKpiTargets.includes(kpiType);
    }, [widgetType, highlightedKpiTargets]);

    const postHighlightKpi = useCallback(async () => {
      const kpiType = getKpiTypeFromWidgetType(widgetType as WidgetType);

      const request = {
        [kpiType]: !isWidgetHighlighted,
      };

      const response = await updateHighlightedKpi(request);

      if (isFailureResponse(response)) {
        throw new Error();
      }
      set({ highlightedKpiTargets: response.data });
      return response.data;
    }, [isWidgetHighlighted]);
    const handleClickHighlightIcon = () => {
      setShowHighlightedWidgetModal(true);
    };
    const { t } = useTranslation();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('mobile'));

    return (
      <RowFlexContainer alignItems="center" padding={[2, 0]} maxWidth={isMobile ? 'initial' : [85]}>
        <RowFlexContainer flex={1} alignItems="center">
          <StyledTypography overflow={'visible'} isDisabled={isPermissionDisabled}>
            {t(translationData.key, translationData.defaultValue)}
          </StyledTypography>
          {shouldShowHighlightIcon && (
            <StyledHighlightIcon
              icon={isWidgetHighlighted ? Icons.STAR_FILLED : Icons.STAR}
              size="small"
              margin={[0, 0, 0, 1]}
              onClick={() => {
                handleClickHighlightIcon();
              }}
              isHighlighted={isWidgetHighlighted}
            />
          )}
          {showHighlightedWidgetModal && (
            <HighlightedWidgetModal
              isOpen={true}
              onClose={() => {
                setShowHighlightedWidgetModal(false);
                resetHighlightKpi();
              }}
              onSubmit={() => {
                triggerHighlightKpi(postHighlightKpi);
              }}
              hasResponse={updatedHighlightKpiTypes !== null}
              hasError={isError}
              hasLoading={isLoading}
              widgetType={widgetType}
              isWidgetHighlighted={isWidgetHighlighted}
              translationData={translationData}
              data-testid="highlighted-widget-modal"
            />
          )}

          {isPermissionDisabled && (
            <Tooltip
              arrowedToolTip
              text={
                <ContainerV2 padding={[1, 1]}>
                  <Typography color="white">
                    {t(
                      widgetAccessControlTranslations.permissionDisabledText.key,
                      widgetAccessControlTranslations.permissionDisabledText.defaultValue,
                    )}
                  </Typography>{' '}
                </ContainerV2>
              }
            >
              <Icon icon={Icons.INFO} size="small" margin={[0, 0, 0, 1]} />
            </Tooltip>
          )}
        </RowFlexContainer>

        <ContainerV2 data-testid={`${widgetType}-switch-container`} padding={[0, 2]}>
          <Switch
            isActive={isSwitchActive}
            label={
              isSwitchActive
                ? t(
                    widgetAccessControlTranslations.switchEnabled.key,
                    widgetAccessControlTranslations.switchEnabled.defaultValue,
                  )
                : t(
                    widgetAccessControlTranslations.switchDisabled.key,
                    widgetAccessControlTranslations.switchDisabled.defaultValue,
                  )
            }
            containerWidth={130}
            disabled={isSwitchDisabled || isPermissionDisabled}
            onClick={() => onSwitchToggle(widgetType)}
          />
        </ContainerV2>
      </RowFlexContainer>
    );
  },
);
